import React, { useMemo } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { MultiValue, MultiValueProps } from 'react-select';
import { useQuery } from '@apollo/client';
import { GET_COHORT_INSPECT_FIELDS } from '../../../../../utils/apollo/cohorts';
import {
  FieldContext,
  ICohortInspectFieldFieldsFragment,
  IGetCohortInspectFieldsQuery,
  IGetCohortInspectFieldsQueryVariables,
} from '../../../../../utils/apollo/graphqlSchema';
import { colors } from '../../../../../config/styles';
import { ReactSelectMenu } from '../../../../../components/Dropdown/StyledReactSelect';
import { BasicEntity } from '../../../../../components/EntityLabels';
import { IReportSettingsFormState } from '../types';

interface IProps {
  settingIndex: number;
}

const renderMultiValue = (
  props: MultiValueProps<ICohortInspectFieldFieldsFragment, true, never>,
) => {
  const { children, removeProps } = props;

  return <BasicEntity onClear={removeProps.onClick}>{children}</BasicEntity>;
};

const CohortInspectReportSettingFields = ({ settingIndex }: IProps) => {
  const { control, watch } = useFormContext<IReportSettingsFormState>();

  const { field, fieldState } = useController({
    name: `inspect.schemaInspectSettings.${settingIndex}.fields`,
    control,
  });
  const { value, onChange } = field;

  const selectedSchema = watch(
    `inspect.schemaInspectSettings.${settingIndex}.schemaKey`,
  );

  const { data, loading } = useQuery<
    IGetCohortInspectFieldsQuery,
    IGetCohortInspectFieldsQueryVariables
  >(GET_COHORT_INSPECT_FIELDS, {
    variables: {
      term: '',
      func: null,
      events: [],
      includeFields: [],
      context: FieldContext.Inspect,
      schemaId: selectedSchema,
    },
    skip: !selectedSchema,
  });

  const fields = data?.fields;

  const handleChange = (
    newOptions: MultiValue<ICohortInspectFieldFieldsFragment>,
  ) => {
    onChange(newOptions ?? []);
  };

  const options: ICohortInspectFieldFieldsFragment[] = useMemo(() => {
    if (!fields?.length) {
      return [];
    }

    return (
      fields
        // filter out all nested fields
        .filter((field) => !field.nestedFieldMetadata)
        .sort((fieldA, fieldB) =>
          // all fact fields should be first in the list
          fieldA.isFactField === fieldB.isFactField
            ? 0
            : fieldA.isFactField
              ? -1
              : 1,
        )
    );
  }, [fields]);

  return (
    <ReactSelectMenu<ICohortInspectFieldFieldsFragment, true, never>
      isMulti={true}
      value={value}
      onChange={handleChange}
      options={options}
      getOptionLabel={(option) => option.name}
      getOptionValue={(option) => option.key}
      isDisabled={!selectedSchema || loading}
      placeholder={'Select Fields'}
      blurInputOnSelect={true}
      components={{ MultiValue: renderMultiValue }}
      styles={{
        control: (base) => {
          const result = {
            ...base,
            minWidth: '100%',
            maxWidth: '100%',
          };

          if (Boolean(fieldState.error)) {
            result.borderColor = colors.mainError;
          }

          return result;
        },
      }}
    />
  );
};

export default CohortInspectReportSettingFields;
